import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { TimeAllocationType } from '../../api/work-periods-client/work-periods-client.type';
import { MetricType, MultipleViewMode, Tab, ViewMode } from '../../containers/process-analysis/process-analysis.type';
import { createSafeJSONStorage } from '../../helpers/storage/persist-storage';
import { isLoggingEnabled, logger } from '../logger/logger';
import { getDefaultComparisonModeFilters, getLastMonthDateRange } from './process-analysis-store.helpers';
import { Panel, type State } from './process-analysis-store.type';

const initialState: State = {
  activeTab: Tab.Portfolios,
  viewMode: ViewMode.MeasureComparison,
  multipleViewMode: MultipleViewMode.MonthlyAverages,
  activePanel: Panel.Filters,
  startDate: getLastMonthDateRange().firstDay,
  endDate: getLastMonthDateRange().lastDay,
  availableMeasures: [],
  chartData: null,
  initiatives: [],
  initiativeCompletionData: null,
  initiativeFocusData: null,
  initiativeCompletionMetric: MetricType.WorkUnits,
  initiativeFocusMetric: MetricType.WorkUnits,
  selectedPortfolios: [],
  selectedTeams: [],
  selectedBoards: [],
  selectedTaskType: null,
  selectedPoints: null,
  selectedAllocationType: null,
  singleModeEntityFilters: {},
  comparisonModeEntityFilters: getDefaultComparisonModeFilters(),
  selectedCapacityTeams: [],
  selectedDataPoint: null,
  selectedTargets: [],
  timeAllocations: [],
  agentVisibleSeries: {
    non_agent_cycle_time: true,
    agent_cycle_time: true,
    agent_adoption_rate: true,
    agent_session_count_overtime: false,
    agent_event_count_overtime: false,
    agent_count: true,
    non_agent_count: true,
  },
  agentViewMode: 'cycle_time',
  agentChartType: 'histogram',
};

/**
 * The only keys this store writes to localStorage: the user's view choices and
 * filter selections, i.e. the state worth restoring on the next visit.
 *
 * This is deliberately an ALLOW-list. The deny-list it replaced kept everything
 * it did not name, which silently persisted the server payloads this store also
 * holds — `initiatives` alone carries five `Record<string, Metric>` time series
 * per initiative, enough to push the blob past the 5 MiB localStorage cap on a
 * large portfolio. Web Storage then throws `QuotaExceededError` on *every*
 * subsequent write for the whole origin, which took the app down with a blank
 * page (the post-login landing route is this page). An allow-list also makes the
 * safe direction the default: a new field added to `State` is not persisted
 * until it is named here.
 *
 * Server data is owned by React Query and refetched on mount — persisting it
 * bought a flash of stale numbers, never correctness.
 */
const PERSISTED_KEYS = [
  'activeTab',
  'viewMode',
  'multipleViewMode',
  'startDate',
  'endDate',
  'initiativeCompletionMetric',
  'initiativeFocusMetric',
  'selectedPortfolios',
  'selectedTeams',
  'selectedBoards',
  'selectedTaskType',
  'selectedPoints',
  'selectedAllocationType',
  'singleModeEntityFilters',
  'comparisonModeEntityFilters',
  'selectedCapacityTeams',
  'agentVisibleSeries',
  'agentViewMode',
  'agentChartType',
] as const satisfies readonly (keyof State)[];

const PERSISTED_KEY_SET: ReadonlySet<string> = new Set(PERSISTED_KEYS);

/**
 * Narrows the `unknown` blob zustand reads back from storage to something
 * enumerable. An array passes, which is harmless: none of its numeric-string keys
 * can be in the allow-list, so it reduces to `{}` like any other junk shape.
 *
 * @param {unknown} value - The deserialized persisted state
 * @returns {boolean} True when the value can be safely enumerated
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Permitted values for the enum-typed persisted keys. */
const PERSISTED_ENUM_VALUES: Partial<Record<keyof State, readonly unknown[]>> = {
  activeTab: Object.values(Tab),
  viewMode: Object.values(ViewMode),
  multipleViewMode: Object.values(MultipleViewMode),
  initiativeCompletionMetric: Object.values(MetricType),
  initiativeFocusMetric: Object.values(MetricType),
  selectedAllocationType: [...Object.values(TimeAllocationType), null],
};

/**
 * Checks one persisted value against the shape of this store's own default for
 * that key, so an allow-listed name cannot smuggle in an incompatible value.
 *
 * Filtering by key alone is not enough. A stored `selectedTeams: null` reaches
 * render-phase selectors that do `selectedTeams[0]` (`available-measures.ts`) and
 * `selectedTeams.length` (`useComparisonMode`), and with no error boundary in the
 * app a TypeError there blanks the page — the same failure this store's quota fix
 * addresses, arriving through the same channel.
 *
 * Deriving the expected shape from `initialState` keeps this honest as `State`
 * grows: a newly allow-listed key is validated without anyone remembering to add
 * a rule. Enum membership is the one thing a default cannot convey, so those keys
 * are listed explicitly above.
 *
 * @param {keyof State} key - The allow-listed key being restored
 * @param {unknown} value - The value found in storage, or in live state
 * @returns {boolean} True when the value is safe to put into state
 */
function isValidPersistedValue(key: keyof State, value: unknown): boolean {
  const permitted = PERSISTED_ENUM_VALUES[key];

  if (permitted) {
    return permitted.includes(value);
  }

  const fallback = initialState[key];

  if (Array.isArray(fallback)) {
    return Array.isArray(value);
  }

  if (fallback !== null && typeof fallback === 'object') {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  if (fallback === null) {
    // A nullable-scalar default cannot tell us the concrete type, so accept null
    // or any primitive and reject containers.
    return value === null || ['string', 'number', 'boolean'].includes(typeof value);
  }

  return typeof value === typeof fallback;
}

/**
 * Reduces a state-shaped object down to `PERSISTED_KEYS`, dropping any value that
 * does not match the shape of its default.
 *
 * The cast is unavoidable: the keys are filtered at runtime against a `Set`, so
 * the compiler cannot track which of them survived.
 *
 * @param {Record<string, unknown>} source - Live state, or a blob read from storage
 * @returns {Partial<State>} Only the allow-listed keys holding usable values
 */
function pickPersistedState(source: Record<string, unknown>): Partial<State> {
  return Object.fromEntries(
    Object.entries(source).filter(
      ([key, value]) => PERSISTED_KEY_SET.has(key) && isValidPersistedValue(key as keyof State, value),
    ),
  ) as Partial<State>;
}

/**
 * Migrates a pre-v1 blob by dropping the server payloads the old deny-list kept.
 *
 * Without this, an existing user's oversized blob would still be merged back into
 * the store on hydration, restoring stale server data and leaving the key too big
 * to be rewritten. zustand calls `setItem` immediately after a successful
 * migration, so returning the trimmed state is what actually reclaims the space —
 * one page load is enough to unstick a browser that is already at its quota.
 *
 * @param {unknown} persistedState - The blob read from localStorage, shape unverified
 * @returns {Partial<State>} The blob reduced to the currently persisted keys
 */
function migrateProcessAnalysisStore(persistedState: unknown): Partial<State> {
  return pickPersistedState(isRecord(persistedState) ? persistedState : {});
}

/**
 * Merges a stored blob over the defaults, allow-listing it again on the way in.
 *
 * `migrate` alone is not enough to keep server payloads out of state: zustand only
 * calls it when the stored `version` is a *number* that differs from ours, so a
 * blob whose `version` is missing or a string bypasses migration entirely and is
 * merged raw — re-restoring the very fields this store stopped persisting. Running
 * the same filter here makes the allow-list authoritative on read as well as write,
 * for every blob regardless of version.
 *
 * `migrate` still earns its keep: it is what makes zustand treat the load as a
 * migration and immediately rewrite the key, which is what reclaims the space on a
 * browser that is already at its quota.
 *
 * @param {unknown} persistedState - The blob read from storage, post-migration if one ran
 * @param {State} currentState - The store's defaults
 * @returns {State} Defaults overlaid with only the allow-listed persisted keys
 */
function mergeProcessAnalysisState(persistedState: unknown, currentState: State): State {
  const persisted = pickPersistedState(isRecord(persistedState) ? persistedState : {});

  return {
    ...currentState,
    ...persisted,
    // `getComparisonModeFilters` indexes this by `Tab` and callers read fields off
    // the result, so a stored object written before a tab existed — or missing one
    // — would otherwise resolve to `undefined` and throw. Backfilling from the
    // defaults keeps every tab present.
    comparisonModeEntityFilters: {
      ...currentState.comparisonModeEntityFilters,
      ...persisted.comparisonModeEntityFilters,
    },
  };
}

const useProcessAnalysisStore = create<State>()(
  devtools(
    persist(
      logger((_) => ({
        ...initialState,
      })),
      {
        name: 'process-analysis-storage',
        storage: createSafeJSONStorage(() => localStorage),
        version: 1,
        migrate: migrateProcessAnalysisStore,
        merge: mergeProcessAnalysisState,
        partialize: (state) => pickPersistedState(state),
      },
    ),
    {
      name: 'Bloomfilter - Process Analysis Store',
      enabled: isLoggingEnabled,
    },
  ),
);

export { PERSISTED_KEYS, mergeProcessAnalysisState, migrateProcessAnalysisStore, useProcessAnalysisStore };
